import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import { makeStyles } from '@material-ui/core/styles'
import { PageHeader } from 'components/page-header'
import { Tabs, Tab } from 'components/tabs'
import InlineCountBadge from 'components/inline-count-badge'
import TablePagination from 'components/pagination/table-pagination'
import { useVirtualCoachingRequests } from './api-hooks'
import VirtualCoachingCoachPlusGate from './coach-plus-gate'
import LoadError from './load-error'
import RequestsTable from './requests-table'
import { useStudentListShellStyles } from 'pages/pga-coach/students/student-list-shell-styles'
import { isRequestTab, partitionRequestsByTab, REQUEST_TABS } from './utils'

const ROWS_PER_PAGE = 25

// Same page container as the Students list: full content width, same gutters.
const useStyles = makeStyles(theme => ({
  container: {
    boxSizing: 'border-box',
    maxWidth: '100%',
    padding: theme.spacing(3, 4.5, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 1, 2),
    },
  },
}))

const VirtualCoachingRequests = () => {
  const classes = useStyles()
  const shellClasses = useStudentListShellStyles()
  const history = useHistory()
  const location = useLocation()
  const [page, setPage] = useState(0)
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useVirtualCoachingRequests()

  const tabParam = new URLSearchParams(location.search).get('tab')
  const tab = isRequestTab(tabParam) ? tabParam : REQUEST_TABS.READY

  useEffect(() => {
    setPage(0)
  }, [tab])

  const requests = (data ?? previousData)?.virtualCoachingRequests
  const partitioned = useMemo(() => partitionRequestsByTab(requests), [
    requests,
  ])

  const tabRequests = partitioned[tab]
  const pageCount = Math.ceil(tabRequests.length / ROWS_PER_PAGE) || 1

  const handleTabChange = (_, nextTab) => {
    if (nextTab === tab) return
    history.push(`${location.pathname}?tab=${nextTab}`)
  }

  const tabs = (
    <Tabs value={tab} onChange={handleTabChange} labelGapSpacing={3}>
      <Tab label="Purchased" value={REQUEST_TABS.PURCHASED} />
      <Tab
        label={
          <InlineCountBadge count={partitioned[REQUEST_TABS.READY].length}>
            Ready to analyze
          </InlineCountBadge>
        }
        value={REQUEST_TABS.READY}
      />
      <Tab label="Complete" value={REQUEST_TABS.COMPLETE} />
    </Tabs>
  )

  const isInitialLoad = loading && !requests

  return (
    <>
      <PageHeader title="Virtual Coaching" tabs={tabs} />
      <Box className={classes.container}>
        {isInitialLoad ? <LinearProgress color="primary" /> : null}
        {!loading && !requests ? (
          <LoadError
            error={error}
            title="We couldn't load your requests"
            onRetry={() => refetch()}
          />
        ) : null}
        {requests ? (
          <Box className={shellClasses.tableShell}>
            {tabRequests.length > ROWS_PER_PAGE ? (
              <Box className={shellClasses.toolbar}>
                <Box className={shellClasses.toolbarActions}>
                  <TablePagination
                    page={page}
                    pageCount={pageCount}
                    rowsPerPage={ROWS_PER_PAGE}
                    totalCount={tabRequests.length}
                    onPageChange={setPage}
                    alwaysShowCount
                  />
                </Box>
              </Box>
            ) : null}
            <RequestsTable
              requests={tabRequests}
              tab={tab}
              page={page}
              rowsPerPage={ROWS_PER_PAGE}
            />
          </Box>
        ) : null}
      </Box>
    </>
  )
}

const VirtualCoachingRequestsPage = () => (
  <VirtualCoachingCoachPlusGate showEmptyRequests>
    <VirtualCoachingRequests />
  </VirtualCoachingCoachPlusGate>
)

export default VirtualCoachingRequestsPage
